import { useState, useMemo } from 'react';
import { VStack, Box, Button, IconButton, Flex, Text } from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, WarningIcon } from '@chakra-ui/icons';

import SectionCard from '../../components/SectionCard';
import PropertyClaimModal from '../../components/PropertyClaimModal';
import ClaimsList from '../../components/ClaimsList';
import FloaterModal from '../../components/FloaterModal';
import WatercraftModal from '../../components/WatercraftModal';
import TrailerModal from '../../components/TrailerModal';
import PropertyDiscountsModal from '../../components/PropertyDiscountsModal';
import {
  createEmptyFloater,
  createEmptyWatercraft,
  createEmptyTrailer,
  createEmptyPropertyClaim,
} from '../../constants/locationOptions';
import { useAnswerCodes, useValidationSchema } from '../../hooks';
import type {
  Location,
  PropertyClaim,
  Floater,
  Watercraft,
  Trailer,
} from 'api';
import type { PropertyInsuranceHistoryData } from '../policy-info/types';

interface RiskFactorsCardProps {
  location: Location;
  locationIndex: number;
  onUpdate: (index: number, field: keyof Location, value: unknown) => void;
  isUpdating?: boolean;
  height?: string;
  locations?: Location[];
  insuranceHistory?: PropertyInsuranceHistoryData;
  onInsuranceHistoryChange?: (
    data: Partial<PropertyInsuranceHistoryData>,
  ) => void;
}

type RiskItemType = 'claim' | 'floater' | 'watercraft' | 'trailer';

const RISK_ITEM_PATHS = {
  claim: 'location/claim',
  floater: 'location/floater',
  watercraft: 'location/watercraft',
  trailer: 'trailer',
} as const;

const RISK_REQUIRED_FIELDS: Record<RiskItemType, string[]> = {
  claim: ['date', 'type_code', 'cause_code', 'payout_amount'],
  floater: ['item_type', 'item_value_cad'],
  watercraft: ['name', 'value_cad'],
  trailer: ['trailer_type'],
};

const RiskFactorsCard = ({
  location,
  locationIndex,
  onUpdate,
  isUpdating,
  height,
  locations = [],
  insuranceHistory,
  onInsuranceHistoryChange,
}: RiskFactorsCardProps) => {
  const { getOptions, getOptionsWithTypes, getLabel } = useAnswerCodes();
  const { isFieldRequired, validateField } = useValidationSchema();

  const [isDiscountsModalOpen, setIsDiscountsModalOpen] = useState(false);
  const [discountData, setDiscountData] =
    useState<PropertyInsuranceHistoryData>(insuranceHistory || {});

  const isMembershipComplete = useMemo(() => {
    if (!insuranceHistory?.membership_discount_membership_group) return true;
    return Boolean(
      insuranceHistory.membership_discount_membership_number &&
      insuranceHistory.membership_discount_membership_effective_date,
    );
  }, [insuranceHistory]);

  const isAdditionalMembershipComplete = useMemo(() => {
    if (!insuranceHistory?.additional_membership_discount_membership_group)
      return true;
    return Boolean(
      insuranceHistory.additional_membership_discount_membership_number &&
      insuranceHistory.additional_membership_discount_membership_effective_date,
    );
  }, [insuranceHistory]);

  const hasDiscountsSet =
    Boolean(insuranceHistory?.membership_discount_membership_group) &&
    isMembershipComplete &&
    isAdditionalMembershipComplete;

  const discountSetByLocationIndex = useMemo(() => {
    if (!hasDiscountsSet) return null;
    return 0;
  }, [hasDiscountsSet]);

  const isDiscountLockedByOther =
    discountSetByLocationIndex !== null &&
    discountSetByLocationIndex !== locationIndex;

  const discountLocationLabel =
    discountSetByLocationIndex !== null
      ? `Location ${discountSetByLocationIndex + 1}`
      : '';

  const handleOpenDiscountsModal = () => {
    setDiscountData(insuranceHistory || {});
    setIsDiscountsModalOpen(true);
  };

  const handleCloseDiscountsModal = () => {
    setIsDiscountsModalOpen(false);
  };

  const handleDiscountChange = (
    data: Partial<PropertyInsuranceHistoryData>,
  ) => {
    setDiscountData((prev) => ({ ...prev, ...data }));
  };

  const handleSaveDiscounts = () => {
    if (onInsuranceHistoryChange) {
      onInsuranceHistoryChange(discountData);
    }
    setIsDiscountsModalOpen(false);
  };

  const getRiskFieldError = (
    itemType: RiskItemType,
    fieldPath: string,
    item: Record<string, unknown>,
  ) => {
    const itemPath = RISK_ITEM_PATHS[itemType];
    return validateField(itemPath, fieldPath, item);
  };

  const isRiskItemIncomplete = (
    itemType: RiskItemType,
    item: PropertyClaim | Floater | Watercraft | Trailer,
  ): boolean => {
    const fields = RISK_REQUIRED_FIELDS[itemType];
    return fields.some((field) =>
      getRiskFieldError(
        itemType,
        field,
        item as unknown as Record<string, unknown>,
      ),
    );
  };
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [editingClaimIndex, setEditingClaimIndex] = useState<number | null>(
    null,
  );
  const [editingClaim, setEditingClaim] = useState<PropertyClaim | null>(null);

  const [isFloaterModalOpen, setIsFloaterModalOpen] = useState(false);
  const [editingFloaterIndex, setEditingFloaterIndex] = useState<number | null>(
    null,
  );
  const [editingFloater, setEditingFloater] = useState<Floater | null>(null);

  const [isWatercraftModalOpen, setIsWatercraftModalOpen] = useState(false);
  const [editingWatercraftIndex, setEditingWatercraftIndex] = useState<
    number | null
  >(null);
  const [editingWatercraft, setEditingWatercraft] = useState<Watercraft | null>(
    null,
  );

  const [isTrailerModalOpen, setIsTrailerModalOpen] = useState(false);
  const [editingTrailerIndex, setEditingTrailerIndex] = useState<number | null>(
    null,
  );
  const [editingTrailer, setEditingTrailer] = useState<Trailer | null>(null);

  const claims = location.claims || [];
  const floaters = location.floaters || [];
  const watercraft = location.watercraft || [];
  const trailers = location.trailers || [];

  const getNextClaimId = () => {
    return claims.length > 0 ? Math.max(...claims.map((c) => c.id)) + 1 : 1;
  };

  const handleAddClaim = () => {
    const newClaim = createEmptyPropertyClaim(getNextClaimId());
    setEditingClaim(newClaim);
    setEditingClaimIndex(null);
    setIsClaimModalOpen(true);
  };

  const handleEditClaim = (index: number, claim: PropertyClaim) => {
    setEditingClaim({ ...claim });
    setEditingClaimIndex(index);
    setIsClaimModalOpen(true);
  };

  const handleDeleteClaim = (index: number) => {
    const updated = claims.filter((_, i) => i !== index);
    onUpdate(locationIndex, 'claims', updated);
  };

  const handleSaveClaim = () => {
    if (!editingClaim) return;

    const claimToSave = { ...editingClaim, fault_percentage: 0 };
    let updated: PropertyClaim[];
    if (editingClaimIndex !== null) {
      updated = [...claims];
      updated[editingClaimIndex] = claimToSave;
    } else {
      updated = [...claims, claimToSave];
    }

    onUpdate(locationIndex, 'claims', updated);
    setIsClaimModalOpen(false);
    setEditingClaim(null);
    setEditingClaimIndex(null);
  };

  const handleCloseModal = () => {
    setIsClaimModalOpen(false);
    setEditingClaim(null);
    setEditingClaimIndex(null);
  };

  const getNextFloaterId = () => {
    return floaters.length > 0 ? Math.max(...floaters.map((f) => f.id)) + 1 : 1;
  };

  const handleAddFloater = () => {
    const newFloater = createEmptyFloater(getNextFloaterId());
    setEditingFloater(newFloater);
    setEditingFloaterIndex(null);
    setIsFloaterModalOpen(true);
  };

  const handleEditFloater = (index: number, floater: Floater) => {
    setEditingFloater({ ...floater });
    setEditingFloaterIndex(index);
    setIsFloaterModalOpen(true);
  };

  const handleDeleteFloater = (index: number) => {
    const updated = floaters.filter((_, i) => i !== index);
    onUpdate(locationIndex, 'floaters', updated);
  };

  const handleSaveFloater = () => {
    if (!editingFloater) return;

    let updated: Floater[];
    if (editingFloaterIndex !== null) {
      updated = [...floaters];
      updated[editingFloaterIndex] = editingFloater;
    } else {
      updated = [...floaters, editingFloater];
    }

    onUpdate(locationIndex, 'floaters', updated);
    setIsFloaterModalOpen(false);
    setEditingFloater(null);
    setEditingFloaterIndex(null);
  };

  const handleCloseFloaterModal = () => {
    setIsFloaterModalOpen(false);
    setEditingFloater(null);
    setEditingFloaterIndex(null);
  };

  const getNextWatercraftId = () => {
    return watercraft.length > 0
      ? Math.max(...watercraft.map((w) => w.id)) + 1
      : 1;
  };

  const handleAddWatercraft = () => {
    const newWatercraft = createEmptyWatercraft(getNextWatercraftId());
    setEditingWatercraft(newWatercraft);
    setEditingWatercraftIndex(null);
    setIsWatercraftModalOpen(true);
  };

  const handleEditWatercraft = (index: number, watercraftItem: Watercraft) => {
    setEditingWatercraft({ ...watercraftItem });
    setEditingWatercraftIndex(index);
    setIsWatercraftModalOpen(true);
  };

  const handleDeleteWatercraft = (index: number) => {
    const updated = watercraft.filter((_, i) => i !== index);
    onUpdate(locationIndex, 'watercraft', updated);
  };

  const handleSaveWatercraft = () => {
    if (!editingWatercraft) return;

    let updated: Watercraft[];
    if (editingWatercraftIndex !== null) {
      updated = [...watercraft];
      updated[editingWatercraftIndex] = editingWatercraft;
    } else {
      updated = [...watercraft, editingWatercraft];
    }

    onUpdate(locationIndex, 'watercraft', updated);
    setIsWatercraftModalOpen(false);
    setEditingWatercraft(null);
    setEditingWatercraftIndex(null);
  };

  const handleCloseWatercraftModal = () => {
    setIsWatercraftModalOpen(false);
    setEditingWatercraft(null);
    setEditingWatercraftIndex(null);
  };

  const getNextTrailerId = () => {
    return trailers.length > 0 ? Math.max(...trailers.map((t) => t.id)) + 1 : 1;
  };

  const handleAddTrailer = () => {
    const newTrailer = createEmptyTrailer(getNextTrailerId());
    setEditingTrailer(newTrailer);
    setEditingTrailerIndex(null);
    setIsTrailerModalOpen(true);
  };

  const handleEditTrailer = (index: number, trailerItem: Trailer) => {
    setEditingTrailer({ ...trailerItem });
    setEditingTrailerIndex(index);
    setIsTrailerModalOpen(true);
  };

  const handleDeleteTrailer = (index: number) => {
    const updated = trailers.filter((_, i) => i !== index);
    onUpdate(locationIndex, 'trailers', updated);
  };

  const handleSaveTrailer = () => {
    if (!editingTrailer) return;

    let updated: Trailer[];
    if (editingTrailerIndex !== null) {
      updated = [...trailers];
      updated[editingTrailerIndex] = editingTrailer;
    } else {
      updated = [...trailers, editingTrailer];
    }

    onUpdate(locationIndex, 'trailers', updated);
    setIsTrailerModalOpen(false);
    setEditingTrailer(null);
    setEditingTrailerIndex(null);
  };

  const handleCloseTrailerModal = () => {
    setIsTrailerModalOpen(false);
    setEditingTrailer(null);
    setEditingTrailerIndex(null);
  };

  const claimTypes = getOptions('claim_type');
  const claimCauses = getOptionsWithTypes('claim_cause');

  const floaterItemTypes = getOptions('floater_item_type');
  const floaterCoverageRequired = getOptions('floater_coverage_required');
  const floaterItemExhibited = getOptions('floater_item_exhibited');

  const watercraftPropulsionTypes = getOptions('watercraft_propulsion_type');
  const watercraftMooringLocations = getOptions('watercraft_mooring_location');
  const watercraftUsageOptions = getOptions('watercraft_watercraft_usage');
  const watercraftWatersNavigated = getOptions('watercraft_waters_navigated');
  const trailerConditionOptions = getOptions(
    'trailer_condition_when_purchased',
  );
  const trailerTypeOptions = getOptions('trailer_trailer_type');
  const trailerRoadLicensedOptions = getOptions(
    'trailer_is_the_trailer_road_licensed',
  );
  const trailerProvinceOptions = getOptions('trailer_province_territory');
  const trailerOccupiedByOwnerOptions = getOptions(
    'trailer_occupied_by_the_owner',
  );
  const trailerPermanentlyParkedOptions = getOptions(
    'trailer_the_trailer_is_permanently_parked',
  );
  const trailerHailCoverageOptions = getOptions(
    'trailer_hail_coverage_required',
  );

  const formatClaimDisplay = (claim: PropertyClaim) => {
    const date = claim.date || 'No date';
    const cause = claim.cause_code
      ? getLabel(claim.cause_code, 'claim_cause')
      : 'No cause';
    const amount = claim.payout_amount ? ` - $${claim.payout_amount}` : '';
    return `${date} - ${cause}${amount}`;
  };

  const renderClaimsItems = () => (
    <ClaimsList
      claims={claims}
      formatDisplay={formatClaimDisplay}
      onEdit={handleEditClaim}
      onDelete={handleDeleteClaim}
      isIncomplete={(claim) => isRiskItemIncomplete('claim', claim)}
      isUpdating={isUpdating}
    />
  );

  const formatFloaterValue = (floater: Floater) => {
    if (floater.item_value_cad) {
      return ` - $${floater.item_value_cad.toLocaleString()}`;
    }
    return '';
  };

  const renderFloatersItems = () => (
    <VStack spacing={1} align="stretch" pl={2}>
      {floaters.map((floater, idx) => {
        const isIncomplete = isRiskItemIncomplete('floater', floater);
        return (
          <Box
            key={floater.id}
            position="relative"
            py={1}
            role="group"
            _hover={{ bg: 'gray.50' }}
            borderRadius="sm"
          >
            <Flex align="center" gap={1}>
              {isIncomplete && (
                <WarningIcon color="orange.500" boxSize={3} flexShrink={0} />
              )}
              <Text
                fontSize="xs"
                color={isIncomplete ? 'orange.700' : 'gray.700'}
              >
                •{' '}
                {floater.item_type
                  ? getLabel(floater.item_type, 'floater_item_type')
                  : 'No type'}
                {floater.item_description
                  ? ` - ${floater.item_description}`
                  : ''}
                {formatFloaterValue(floater)}
              </Text>
            </Flex>
            <Flex
              position="absolute"
              top={0}
              right={0}
              gap={0}
              opacity={0}
              _groupHover={{ opacity: 1 }}
              bg="gray.50"
              borderRadius="sm"
            >
              <IconButton
                aria-label="Edit"
                icon={<EditIcon />}
                size="xs"
                variant="ghost"
                onClick={() => handleEditFloater(idx, floater)}
                minW="auto"
                h="auto"
                p={1}
              />
              <IconButton
                aria-label="Delete"
                icon={<DeleteIcon />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => handleDeleteFloater(idx)}
                isDisabled={isUpdating}
                minW="auto"
                h="auto"
                p={1}
              />
            </Flex>
          </Box>
        );
      })}
    </VStack>
  );

  const formatWatercraftValue = (watercraftItem: Watercraft) => {
    if (watercraftItem.value_cad) {
      return ` - $${Number(watercraftItem.value_cad).toLocaleString()}`;
    }
    return '';
  };

  const renderWatercraftItems = () => (
    <VStack spacing={1} align="stretch" pl={2}>
      {watercraft.map((watercraftItem, idx) => {
        const isIncomplete = isRiskItemIncomplete('watercraft', watercraftItem);
        return (
          <Box
            key={watercraftItem.id}
            position="relative"
            py={1}
            role="group"
            _hover={{ bg: 'gray.50' }}
            borderRadius="sm"
          >
            <Flex align="center" gap={1}>
              {isIncomplete && (
                <WarningIcon color="orange.500" boxSize={3} flexShrink={0} />
              )}
              <Text
                fontSize="xs"
                color={isIncomplete ? 'orange.700' : 'gray.700'}
              >
                • {watercraftItem.name || 'Unnamed'}
                {watercraftItem.make_model
                  ? ` - ${watercraftItem.make_model}`
                  : ''}
                {formatWatercraftValue(watercraftItem)}
              </Text>
            </Flex>
            <Flex
              position="absolute"
              top={0}
              right={0}
              gap={0}
              opacity={0}
              _groupHover={{ opacity: 1 }}
              bg="gray.50"
              borderRadius="sm"
            >
              <IconButton
                aria-label="Edit"
                icon={<EditIcon />}
                size="xs"
                variant="ghost"
                onClick={() => handleEditWatercraft(idx, watercraftItem)}
                minW="auto"
                h="auto"
                p={1}
              />
              <IconButton
                aria-label="Delete"
                icon={<DeleteIcon />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => handleDeleteWatercraft(idx)}
                isDisabled={isUpdating}
                minW="auto"
                h="auto"
                p={1}
              />
            </Flex>
          </Box>
        );
      })}
    </VStack>
  );

  const formatTrailerValue = (trailerItem: Trailer) => {
    if (trailerItem.replacement_value_cad) {
      return ` - $${Number(trailerItem.replacement_value_cad).toLocaleString()}`;
    }
    return '';
  };

  const getTrailerTypeLabel = (trailerType: string) => {
    const option = trailerTypeOptions.find((opt) => opt.value === trailerType);
    return option?.label || trailerType || 'Unknown Type';
  };

  const renderTrailerItems = () => (
    <VStack spacing={1} align="stretch" pl={2}>
      {trailers.map((trailerItem, idx) => {
        const isIncomplete = isRiskItemIncomplete('trailer', trailerItem);
        return (
          <Box
            key={trailerItem.id}
            position="relative"
            py={1}
            role="group"
            _hover={{ bg: 'gray.50' }}
            borderRadius="sm"
          >
            <Flex align="center" gap={1}>
              {isIncomplete && (
                <WarningIcon color="orange.500" boxSize={3} flexShrink={0} />
              )}
              <Text
                fontSize="xs"
                color={isIncomplete ? 'orange.700' : 'gray.700'}
              >
                • {getTrailerTypeLabel(trailerItem.trailer_type)}
                {trailerItem.make_model ? ` - ${trailerItem.make_model}` : ''}
                {formatTrailerValue(trailerItem)}
              </Text>
            </Flex>
            <Flex
              position="absolute"
              top={0}
              right={0}
              gap={0}
              opacity={0}
              _groupHover={{ opacity: 1 }}
              bg="gray.50"
              borderRadius="sm"
            >
              <IconButton
                aria-label="Edit"
                icon={<EditIcon />}
                size="xs"
                variant="ghost"
                onClick={() => handleEditTrailer(idx, trailerItem)}
                minW="auto"
                h="auto"
                p={1}
              />
              <IconButton
                aria-label="Delete"
                icon={<DeleteIcon />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={() => handleDeleteTrailer(idx)}
                isDisabled={isUpdating}
                minW="auto"
                h="auto"
                p={1}
              />
            </Flex>
          </Box>
        );
      })}
    </VStack>
  );

  interface RiskSection {
    key: string;
    title: string;
    items: unknown[];
    onAdd: () => void;
    renderItems: () => React.ReactNode;
  }

  const riskSections: RiskSection[] = [
    {
      key: 'claims',
      title: 'Claims',
      items: claims,
      onAdd: handleAddClaim,
      renderItems: renderClaimsItems,
    },
    {
      key: 'discounts',
      title: 'Discounts',
      items:
        hasDiscountsSet ||
        Boolean(insuranceHistory?.membership_discount_membership_group)
          ? [1]
          : [],
      onAdd: handleOpenDiscountsModal,
      renderItems: () => {
        const hasGroup = Boolean(
          insuranceHistory?.membership_discount_membership_group,
        );
        if (!hasGroup) return null;
        const isIncomplete =
          !isMembershipComplete || !isAdditionalMembershipComplete;
        return (
          <Flex align="center" gap={1} pl={2}>
            {isIncomplete && (
              <WarningIcon color="orange.500" boxSize={3} flexShrink={0} />
            )}
            <Text
              fontSize="xs"
              color={isIncomplete ? 'orange.700' : 'gray.600'}
            >
              • Membership discounts{' '}
              {isIncomplete ? 'incomplete' : 'configured'}
            </Text>
          </Flex>
        );
      },
    },
    {
      key: 'floaters',
      title: 'Floaters',
      items: floaters,
      onAdd: handleAddFloater,
      renderItems: renderFloatersItems,
    },
    {
      key: 'watercraft',
      title: 'Watercraft',
      items: watercraft,
      onAdd: handleAddWatercraft,
      renderItems: renderWatercraftItems,
    },
    {
      key: 'trailers',
      title: 'Trailers',
      items: trailers,
      onAdd: handleAddTrailer,
      renderItems: renderTrailerItems,
    },
  ];

  return (
    <>
      <SectionCard title="Risk Factors" height={height}>
        <VStack spacing={4} align="stretch">
          {riskSections.map((section) => {
            const isDiscountSection = section.key === 'discounts';
            const isDiscountDisabled =
              isDiscountSection && isDiscountLockedByOther;

            return (
              <Box key={section.key}>
                {isDiscountDisabled ? (
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    textAlign="center"
                    py={2}
                  >
                    Discounts set on {discountLocationLabel}
                  </Text>
                ) : (
                  <Button
                    size="sm"
                    variant="solid"
                    colorScheme="brand"
                    borderRadius="full"
                    leftIcon={<AddIcon boxSize={3} />}
                    onClick={section.onAdd}
                    isDisabled={isUpdating}
                    mb={2}
                    w="100%"
                    justifyContent="center"
                  >
                    {section.title}
                  </Button>
                )}
                {section.items.length > 0 && section.renderItems()}
              </Box>
            );
          })}
        </VStack>
      </SectionCard>

      {editingClaim && (
        <PropertyClaimModal
          isOpen={isClaimModalOpen}
          onClose={handleCloseModal}
          claim={editingClaim}
          onChange={setEditingClaim}
          onSave={handleSaveClaim}
          claimTypes={claimTypes}
          claimCauses={claimCauses}
        />
      )}

      {editingFloater && (
        <FloaterModal
          isOpen={isFloaterModalOpen}
          onClose={handleCloseFloaterModal}
          floater={editingFloater}
          onChange={setEditingFloater}
          onSave={handleSaveFloater}
          itemTypes={floaterItemTypes}
          coverageRequiredOptions={floaterCoverageRequired}
          itemExhibitedOptions={floaterItemExhibited}
        />
      )}

      {editingWatercraft && (
        <WatercraftModal
          isOpen={isWatercraftModalOpen}
          onClose={handleCloseWatercraftModal}
          watercraft={editingWatercraft}
          onChange={setEditingWatercraft}
          onSave={handleSaveWatercraft}
          propulsionTypeOptions={watercraftPropulsionTypes}
          mooringLocationOptions={watercraftMooringLocations}
          watercraftUsageOptions={watercraftUsageOptions}
          watersNavigatedOptions={watercraftWatersNavigated}
          trailerConditionOptions={trailerConditionOptions}
          isNameRequired={isFieldRequired(
            'location/watercraft',
            'name',
            editingWatercraft as unknown as Record<string, unknown>,
          )}
          isValueRequired={isFieldRequired(
            'location/watercraft',
            'value_cad',
            editingWatercraft as unknown as Record<string, unknown>,
          )}
        />
      )}

      {editingTrailer && (
        <TrailerModal
          isOpen={isTrailerModalOpen}
          onClose={handleCloseTrailerModal}
          trailer={editingTrailer}
          onChange={setEditingTrailer}
          onSave={handleSaveTrailer}
          trailerTypeOptions={trailerTypeOptions}
          conditionOptions={trailerConditionOptions}
          roadLicensedOptions={trailerRoadLicensedOptions}
          provinceOptions={trailerProvinceOptions}
          occupiedByOwnerOptions={trailerOccupiedByOwnerOptions}
          permanentlyParkedOptions={trailerPermanentlyParkedOptions}
          hailCoverageOptions={trailerHailCoverageOptions}
        />
      )}

      <PropertyDiscountsModal
        isOpen={isDiscountsModalOpen}
        onClose={handleCloseDiscountsModal}
        discountData={discountData}
        onChange={handleDiscountChange}
        onSave={handleSaveDiscounts}
      />
    </>
  );
};

export default RiskFactorsCard;
